/**
 * GameInviteCard — what a `/poker`-style slash command leaves in the thread.
 *
 * The room already exists by the time this renders, so Join is a plain
 * navigation: the game's room route auto-joins on mount, which is what makes
 * "everyone taps and ends up at the same table" work without an accept step.
 */

import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import type { GameInviteContent } from "../types";
import { inviteRoomNoun, safeInvitePath } from "../commands/invite-path";

/**
 * Fixed height, so the virtualized list's estimate is exact rather than
 * corrected-after-paint. Mirrored by GAME_INVITE_CARD_HEIGHT in use-pretext.ts —
 * change the two together.
 */
export const INVITE_CARD_HEIGHT = 128;

interface GameInviteCardProps {
  content: GameInviteContent;
  /** Compact layout renders it flush; bubbles layout gives it a small inset. */
  compact?: boolean;
}

export function GameInviteCard({ content, compact }: GameInviteCardProps) {
  const navigate = useNavigate();
  const accent = content.accent || "#8b95a1";
  const path = safeInvitePath(content.path);

  return (
    <div
      className="relative overflow-hidden rounded-2xl border bg-[#14181d]"
      style={{
        width: 320,
        maxWidth: "100%",
        height: INVITE_CARD_HEIGHT,
        borderColor: `${accent}38`,
        marginTop: compact ? 6 : 0,
      }}
    >
      {/* Accent wash — keeps the card tied to the game without a loud fill. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(120% 100% at 0% 0%, ${accent}26 0%, transparent 62%)`,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${accent}, ${accent}00)` }}
      />

      <div className="relative flex h-full flex-col justify-between p-3">
        <div className="flex items-start gap-2.5">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[17px]"
            style={{ backgroundColor: `${accent}1f`, boxShadow: `inset 0 0 0 1px ${accent}45` }}
          >
            {content.icon}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-semibold leading-5 text-white/92">
              {content.gameName}
            </p>
            <p className="line-clamp-2 text-[11.5px] leading-4 text-white/45">
              {content.tagline ?? "Tap to join the room."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[10.5px] uppercase tracking-[0.08em] text-white/30">
            {content.roomId ? (
              <span className="font-mono">
                {inviteRoomNoun(content.game)} {content.roomId}
              </span>
            ) : (
              content.subtitle ?? "Open the game"
            )}
          </span>
          <button
            type="button"
            disabled={!path}
            onClick={() => path && navigate(path)}
            className="inline-flex shrink-0 items-center gap-1 rounded-full px-3.5 py-1.5 text-[12px] font-semibold text-[#0b0d10] transition active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-40"
            style={{ backgroundColor: accent }}
          >
            {content.cta ?? "Join"}
            <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
